/**
 * Phase 21 — RecapCard banner.
 *
 * Small celebratory banner that appears on the Cards screen for ~7
 * days after a recap is generated. Tapping it opens the full Recap
 * page (/recap/:id). Pulls the most-recent recap via
 * useCustomerRecaps({limit:1}) and short-circuits if the latest is
 * older than RECENT_WINDOW_DAYS.
 */
import { Link } from "wouter";
import { useCustomerRecaps } from "@orbit/api-client-react";

const RECENT_WINDOW_MS = 7 * 24 * 60 * 60 * 1000;

export function RecapCard({ enabled }: { enabled: boolean }) {
  const recapsQ = useCustomerRecaps({ limit: 1 }, { enabled });
  const latest = recapsQ.data?.items[0];
  if (!latest) return null;

  const ageMs = Date.now() - new Date(latest.generatedAt).getTime();
  if (ageMs > RECENT_WINDOW_MS || ageMs < 0) return null;

  const isAnnual = latest.kind === "ANNUAL";
  const periodLabel = formatPeriod(latest);

  return (
    <Link
      href={`/recap/${latest.id}`}
      data-testid="recap-card-banner"
      className="block rounded-xl bg-gradient-to-br from-navy to-navy/80 text-white p-4 shadow-md hover:shadow-lg transition-shadow"
    >
      <div className="flex items-center gap-3">
        <div className="flex-shrink-0 w-12 h-12 rounded-full bg-amber flex items-center justify-center text-navy text-xl font-bold">
          {isAnnual ? "★" : "✨"}
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-xs uppercase tracking-wide text-amber/90">
            {isAnnual ? "Year in Orbit" : "Monthly recap"}
          </div>
          <div className="text-base font-semibold truncate">
            Your {periodLabel} is ready
          </div>
          <div className="text-xs text-white/70">
            Tap to see what you earned
          </div>
        </div>
        <div className="flex-shrink-0 text-amber">›</div>
      </div>
    </Link>
  );
}

const PH_MONTH_NAMES = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

function formatPeriod(item: { kind: string; periodStart: string }): string {
  const d = new Date(item.periodStart);
  // periodStart is UTC instant of PH-local midnight; shift +8h then read UTC parts.
  const local = new Date(d.getTime() + 8 * 3_600_000);
  const y = local.getUTCFullYear();
  if (item.kind === "ANNUAL") return `${y} recap`;
  const m = local.getUTCMonth();
  return `${PH_MONTH_NAMES[m]} ${y} recap`;
}
